import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { http } from '../../lib/api';
import { downloadAllCsv, MAX_EXPORT_ROWS } from '../../lib/csv';
import { AppLayout } from '../../components/layout';
import { Button, Card } from '../../components/core';
import { DataTable, type Column, Pagination, SpinnerBlock } from '../../components/feedback';
import { useUiStore } from '../../store/ui';
import { ADMIN_NAV } from './nav';

const PAGE_SIZE = 25;
const num = (n: number) => n.toLocaleString('en-IN');

export interface ReportPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ReportFilter {
  /** Query-string key, e.g. `status`. */
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

/**
 * Shell shared by the plain admin report pages (subscriptions, streams, audit
 * log) — toolbar, pagination and a full-result CSV export. Reports that need
 * their own KPI row (transactions) build a page directly instead.
 */
export function ReportList<T>(props: {
  title: string;
  subtitle: string;
  endpoint: string;
  columns: Column<T>[];
  csv: { filename: string; header: string[]; toRow: (row: T) => (string | number | null)[] };
  searchPlaceholder?: string;
  filters?: ReportFilter[];
  emptyText?: string;
  actions?: ReactNode;
  /**
   * Re-poll the endpoint on this interval (ms). Set by live pages such as
   * stream monitoring, where numbers that only refresh on navigation are
   * worthless; every static report page leaves it unset.
   */
  autoRefreshMs?: number;
}) {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<T[]>([]);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const toast = useUiStore((s) => s.toast);

  // The server only sees the debounced value.
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setTerm(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const query = useCallback(() => {
    const q: Record<string, string | number> = { page, pageSize: PAGE_SIZE };
    if (term) q.search = term;
    for (const [k, v] of Object.entries(active)) if (v) q[k] = v;
    return q;
  }, [page, term, active]);

  const load = useCallback(() => {
    setLoading(true);
    http
      .get<ReportPage<T>>(props.endpoint, query())
      .then((d) => {
        setRows(d.items);
        setTotalPages(d.totalPages);
        setTotal(d.total);
      })
      .catch((e) => setError((e as Error).message ?? 'Failed'))
      .finally(() => setLoading(false));
    // `props.endpoint` is a literal on every caller.
  }, [props.endpoint, query]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!props.autoRefreshMs) return;
    const t = setInterval(() => void load(), props.autoRefreshMs);
    return () => clearInterval(t);
  }, [props.autoRefreshMs, load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const base = query();
      const { written, truncated } = await downloadAllCsv<T>({
        filename: props.csv.filename,
        header: props.csv.header,
        fetchPage: (p) => http.get<ReportPage<T>>(props.endpoint, { ...base, page: p, pageSize: 100 }),
        toRow: props.csv.toRow,
      });
      toast(
        truncated ? 'warning' : 'success',
        `Exported ${num(written)} row${written === 1 ? '' : 's'}`,
        truncated ? `Capped at ${num(MAX_EXPORT_ROWS)} rows — narrow the filters for a complete file.` : undefined,
      );
    } catch (e) {
      toast('danger', 'Export failed', (e as Error).message);
    }
    setExporting(false);
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div>
            <h1 className="page-title">{props.title}</h1>
            <p className="page-subtitle">{props.subtitle}</p>
          </div>
          <div className="flex gap-2">
            {props.actions}
            <Button variant="secondary" onClick={exportCsv} disabled={exporting || !total}>
              {exporting ? 'Exporting…' : '⭳ Download CSV'}
            </Button>
          </div>
        </div>
        {error && <div className="alert danger">{error}</div>}

        {(props.searchPlaceholder || props.filters) && (
          <div className="toolbar">
            {props.searchPlaceholder && (
              <div className="field grow">
                <input
                  className="input"
                  placeholder={props.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
            {props.filters?.map((f) => (
              <div className="field" key={f.key} style={{ minWidth: 150 }}>
                <select
                  className="input"
                  aria-label={f.label}
                  value={active[f.key] ?? ''}
                  onChange={(e) => {
                    setActive((a) => ({ ...a, [f.key]: e.target.value }));
                    setPage(1);
                  }}
                >
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {loading && !rows.length ? <SpinnerBlock /> : (
          <Card>
            <DataTable columns={props.columns} rows={rows} />
            {!rows.length && <p className="text-muted text-sm" style={{ margin: 0 }}>{props.emptyText ?? 'Nothing to show.'}</p>}
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </Card>
        )}
      </div>
    </AppLayout>
  );
}